import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Landing from './pages/Landing';
import Login from './pages/Login';
import RoleSelection from './pages/RoleSelection';
import RegisterParent from './pages/RegisterParent';
import RegisterTutor from './pages/RegisterTutor';
import RegisterCoordinator from './pages/RegisterCoordinator';
import RegisterSchool from './pages/RegisterSchool';
import ParentDashboard from './pages/ParentDashboard';
import TutorDashboard from './pages/TutorDashboard';
import TutorProfile from './pages/TutorProfile';
import CoordinatorDashboard from './pages/CoordinatorDashboard';
import CoordinatorProfile from './pages/CoordinatorProfile';
import AdminDashboard from './pages/AdminDashboard';
import SchoolDashboard from './pages/SchoolDashboard';
import AdminProfile from './pages/AdminProfile';
import StudentDashboard from './pages/StudentDashboard';
import StudentProfile from './pages/StudentProfile';
import ParentProfile from './pages/ParentProfile';
import LogBoard from './pages/LogBoard';
import AwaitingApproval from './pages/AwaitingApproval';
import { Toaster } from './components/ui/sonner';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Set up axios interceptor for test tokens
axios.interceptors.request.use((config) => {
  const testToken = localStorage.getItem('test_session_token');
  if (testToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${testToken}`;
  }
  return config;
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState(null);

  useEffect(() => {
    // Fire-and-forget: warm the Render backend (free tier spins down after 15min
    // idle and cold-starts take ~25-40s). Triggering this immediately means by the
    // time a visitor clicks "Login" / "Get Started", the backend is awake.
    axios.get(`${API}/health`).catch(() => {});

    // Check for session_id in URL fragment (returning from Google OAuth redirect)
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      processSessionId(sessionId);
      // Clean URL
      window.history.replaceState(null, '', window.location.pathname);
    } else if (localStorage.getItem('test_session_token')) {
      // Returning logged-in user — check their session before rendering (so
      // we can route them to their dashboard, not the landing page).
      checkExistingSession();
    } else {
      // First-time visitor / logged-out — render the landing page immediately.
      // Do NOT block on /auth/me; that call is what made cold-starts feel like
      // a 25-second blank screen.
      setLoading(false);
    }
  }, []);

  const processSessionId = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/auth/session`, {
        headers: { 'X-Session-ID': sessionId }
      });
      
      const { session_token, user: userData } = response.data;
      
      // Set cookie (works on most browsers)
      document.cookie = `session_token=${session_token}; path=/; max-age=${7 * 24 * 60 * 60}; secure; samesite=none`;
      // ALSO persist to localStorage as a Bearer-token fallback. Many mobile browsers
      // (Android Chrome strict mode, Safari, incognito) block third-party cookies on
      // cross-domain requests (Vercel frontend → Render backend), which causes
      // subsequent authenticated calls (e.g., /users/register/coordinator) to 401.
      // The axios interceptor below picks up this token and sends it as
      // `Authorization: Bearer …` so auth still works without cookies.
      localStorage.setItem('test_session_token', session_token);
      
      setSessionToken(session_token);
      setUser(userData);
    } catch (error) {
      console.error('Failed to process session:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkExistingSession = async () => {
    try {
      // Check for test token in localStorage first
      const testToken = localStorage.getItem('test_session_token');
      const headers = {};
      
      if (testToken) {
        headers['Authorization'] = `Bearer ${testToken}`;
      }
      
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: testToken ? headers : undefined
      });
      setUser(response.data);
      setSessionToken(testToken || response.data.session_token);
    } catch (error) {
      console.log('No existing session');
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem('test_session_token');
      setUser(null);
      setSessionToken(null);
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Derive an "awaiting admin approval" state.
  // After /users/register/coordinator (and future /register/tutor), the backend
  // keeps role="pending" but populates pending_roles=["coordinator"] (or "tutor")
  // and approval_status="pending". For legacy users who registered before the
  // approval gate existed, we also gate role==="coordinator" with a non-approved
  // approval_status.
  const pendingApprovalRole =
    user && (
      (Array.isArray(user.pending_roles) && user.pending_roles.length > 0 && user.pending_roles[0])
      || ((user.role === 'coordinator' || user.role === 'tutor')
          && user.approval_status && user.approval_status !== 'approved'
          && user.approval_status !== null
          ? user.role
          : null)
    );

  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={
            !user ? <Landing /> :
            pendingApprovalRole ? <Navigate to="/awaiting-approval" /> :
            user.role === 'pending' ? <Navigate to="/role-selection" /> :
            <Navigate to="/dashboard" />
          } />
          <Route path="/login" element={!user ? <Login /> : <Navigate to="/dashboard" />} />
          <Route path="/awaiting-approval" element={
            user && pendingApprovalRole
              ? <AwaitingApproval user={user} logout={logout} role={pendingApprovalRole} />
              : <Navigate to="/" />
          } />
          <Route path="/role-selection" element={
            user && user.role === 'pending' && !pendingApprovalRole
              ? <RoleSelection user={user} />
              : <Navigate to="/" />
          } />
          <Route path="/register/parent" element={user && user.role === 'pending' && !pendingApprovalRole ? <RegisterParent setUser={setUser} /> : <Navigate to="/" />} />
          <Route path="/register/tutor" element={user && user.role === 'pending' && !pendingApprovalRole ? <RegisterTutor setUser={setUser} /> : <Navigate to="/" />} />
          <Route path="/register/coordinator" element={user && user.role === 'pending' && !pendingApprovalRole ? <RegisterCoordinator setUser={setUser} /> : <Navigate to="/" />} />
          <Route path="/register/school" element={user && user.role === 'pending' && !pendingApprovalRole ? <RegisterSchool setUser={setUser} /> : <Navigate to="/" />} />
          <Route path="/dashboard" element={
            !user ? <Navigate to="/" /> :
            pendingApprovalRole ? <Navigate to="/awaiting-approval" /> :
            user.role === 'parent' ? <ParentDashboard user={user} logout={logout} /> :
            user.role === 'student' ? <StudentDashboard user={user} logout={logout} /> :
            user.role === 'tutor' ? <TutorDashboard user={user} logout={logout} /> :
            user.role === 'coordinator' ? <CoordinatorDashboard user={user} logout={logout} /> :
            (user.role === 'admin' || user.role === 'RSN') ? <AdminDashboard user={user} logout={logout} /> :
            user.role === 'school' ? <SchoolDashboard user={user} logout={logout} /> :
            <Navigate to="/role-selection" />
          } />
          <Route path="/student/:studentId" element={user ? <StudentDashboard user={user} logout={logout} /> : <Navigate to="/" />} />
          <Route path="/logboard/:batchId" element={user ? <LogBoard user={user} logout={logout} /> : <Navigate to="/" />} />
          <Route path="/profile" element={
            !user ? <Navigate to="/" /> :
            user.role === 'tutor' ? <TutorProfile user={user} logout={logout} /> :
            user.role === 'coordinator' ? <CoordinatorProfile user={user} logout={logout} /> :
            user.role === 'parent' ? <ParentProfile user={user} logout={logout} /> :
            user.role === 'student' ? <StudentProfile user={user} logout={logout} /> :
            (user.role === 'admin' || user.role === 'RSN') ? <AdminProfile user={user} logout={logout} /> :
            <Navigate to="/dashboard" />
          } />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </>
  );
}

export default App;
